import aiService from './aiService';

/**
 * Project AI Service
 * Provides AI-powered insights and predictions for projects
 */
class ProjectAIService {
  /**
   * Generate AI insights for a project
   * @param {Object} project - Project data
   * @returns {Promise<string>} AI-generated insight
   */
  async generateProjectInsight(project) {
    try {
      // Calculate project health score
      const progress = parseFloat(project.progress || 0);
      const hasContractor = !!project.contractor?.name;
      const budget = parseFloat(project.budget || 0);
      
      // Generate context-aware insights
      if (progress < 25 && project.status === 'in_progress') {
        return "Project is in early stages. Consider setting up regular check-ins with the contractor.";
      }
      
      if (progress >= 25 && progress < 50) {
        return "Project is progressing steadily. Monitor milestone completion to stay on track.";
      }
      
      if (progress >= 50 && progress < 75) {
        return "Project is over halfway complete. Review budget utilization and timeline.";
      }
      
      if (progress >= 75 && progress < 100) {
        return "Project is nearing completion. Prepare for final inspections and closeout.";
      }
      
      if (!hasContractor && project.status === 'open_for_bids') {
        return "Actively seeking contractors. Consider reviewing and responding to proposals.";
      }
      
      if (budget === 0) {
        return "Budget not set. Add project budget to enable better financial tracking.";
      }
      
      return null;
    } catch (error) {
      console.error('Error generating project insight:', error);
      return null;
    }
  }

  /**
   * Analyze project risk
   * @param {Object} project - Project data
   * @returns {Promise<Object>} Risk analysis
   */
  async analyzeProjectRisk(project) {
    try {
      const progress = parseFloat(project.progress || 0);
      const hasContractor = !!project.contractor?.name;
      const budget = parseFloat(project.budget || 0);
      
      let riskLevel = 'low';
      let riskFactors = [];
      
      // Check for risk factors
      if (progress < 30 && project.status === 'in_progress') {
        riskLevel = 'medium';
        riskFactors.push('Low progress for active project');
      }
      
      if (!hasContractor && project.status === 'in_progress') {
        riskLevel = 'high';
        riskFactors.push('No contractor assigned');
      }
      
      if (budget === 0 || !budget) {
        riskLevel = 'medium';
        riskFactors.push('Budget not defined');
      }
      
      return {
        level: riskLevel,
        factors: riskFactors,
        score: riskLevel === 'high' ? 75 : riskLevel === 'medium' ? 50 : 25
      };
    } catch (error) {
      console.error('Error analyzing project risk:', error);
      return { level: 'unknown', factors: [], score: 0 };
    }
  }

  /**
   * Predict project completion date
   * @param {Object} project - Project data
   * @returns {Promise<string>} Predicted completion date
   */
  async predictCompletionDate(project) {
    try {
      const progress = parseFloat(project.progress || 0);
      
      if (progress === 0) {
        return 'Unable to predict - project not started';
      }
      
      // Simple prediction based on current progress
      // In production, this would use more sophisticated ML models
      const daysElapsed = 30; // Placeholder - would calculate from actual dates
      const daysPerPercent = daysElapsed / progress;
      const remainingPercent = 100 - progress;
      const estimatedDaysRemaining = Math.round(daysPerPercent * remainingPercent);
      
      const completionDate = new Date();
      completionDate.setDate(completionDate.getDate() + estimatedDaysRemaining);
      
      return completionDate.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch (error) {
      console.error('Error predicting completion date:', error);
      return 'Unable to predict';
    }
  }

  /**
   * Generate smart suggestions for project
   * @param {Object} project - Project data
   * @returns {Promise<Array>} Array of suggestions
   */
  async generateProjectSuggestions(project) {
    try {
      const suggestions = [];
      const progress = parseFloat(project.progress || 0);
      const hasContractor = !!project.contractor?.name;
      
      if (!hasContractor) {
        suggestions.push({
          type: 'action',
          priority: 'high',
          message: 'Assign a contractor to begin work',
          action: 'assign_contractor'
        });
      }
      
      if (progress === 0 && hasContractor) {
        suggestions.push({
          type: 'action',
          priority: 'medium',
          message: 'Create milestones to track progress',
          action: 'create_milestones'
        });
      }
      
      if (progress > 0 && progress < 100) {
        suggestions.push({
          type: 'insight',
          priority: 'low',
          message: 'Schedule a progress review meeting',
          action: 'schedule_meeting'
        });
      }
      
      return suggestions;
    } catch (error) {
      console.error('Error generating suggestions:', error);
      return [];
    }
  }

  /**
   * Enrich projects with AI insights
   * @param {Array} projects - Array of projects
   * @returns {Promise<Array>} Projects with AI insights
   */
  async enrichProjectsWithAI(projects) {
    try {
      const enrichedProjects = await Promise.all(
        projects.map(async (project) => {
          const insight = await this.generateProjectInsight(project);
          const risk = await this.analyzeProjectRisk(project);
          
          return {
            ...project,
            aiInsight: insight,
            riskAnalysis: risk
          };
        })
      );
      
      return enrichedProjects;
    } catch (error) {
      console.error('Error enriching projects with AI:', error);
      return projects; // Return original projects if AI enrichment fails
    }
  }
}

export default new ProjectAIService();
